/**
 * FERP Portal - Error Tracing Hook
 *
 * Monitors GraphQL errors from Apollo Router and backend services,
 * providing detailed error context for debugging.
 *
 * Captures:
 * - GraphQL validation errors (GRAPHQL_VALIDATION_FAILED)
 * - Backend error codes (FERP-xxxx)
 * - HTTP errors (4xx, 5xx)
 * - Error trends over time
 */

import { useState, useEffect, useCallback } from 'react';

export interface ErrorTrace {
  code: string;
  count: number;
  percentage: number;
  category: 'validation' | 'backend' | 'http' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  timestamp: Date;
}

export interface ErrorSummary {
  totalErrors: number;
  errorRate: number;
  byCategory: Record<string, number>;
  bySeverity: Record<string, number>;
  recentErrors: ErrorTrace[];
  topErrors: ErrorTrace[];
  allErrors: ErrorTrace[]; // All errors for full table display
}

interface ParsedErrors {
  [code: string]: number;
}

/**
 * Categorize error by code
 */
function categorizeError(code: string): ErrorTrace['category'] {
  if (code.includes('VALIDATION') || code.includes('GRAPHQL_')) return 'validation';
  if (code.startsWith('FERP-')) return 'backend';
  if (code.match(/^[45]\d{2}$/)) return 'http';
  return 'unknown';
}

/**
 * Determine error severity
 */
function getErrorSeverity(code: string, count: number): ErrorTrace['severity'] {
  // Critical: Backend 5xxx errors or high count
  if (code.startsWith('FERP-5') || count > 50) return 'critical';

  // High: Backend 4xxx errors or validation errors with high count
  if (code.startsWith('FERP-4') || (code.includes('VALIDATION') && count > 20)) return 'high';

  // Medium: Most validation errors or moderate backend errors
  if (code.includes('VALIDATION') || code.startsWith('FERP-')) return 'medium';

  return 'low';
}

/**
 * Get human-readable error description
 */
function getErrorDescription(code: string): string {
  const descriptions: Record<string, string> = {
    'GRAPHQL_VALIDATION_FAILED': 'GraphQL query validation failed - invalid schema or types',
    'VALIDATION_INVALID_TYPE_VARIABLE': 'Invalid variable type in GraphQL query',
    'FERP-5002': 'Backend repository error - database operation failed',
    'FERP-9001': 'Backend service error - internal logic failure',
    'FERP-1001': 'Entity not found',
    'FERP-2001': 'Repository error',
    'FERP-3001': 'Validation error',
    '400': 'Bad Request - malformed query',
    '401': 'Unauthorized - missing authentication',
    '403': 'Forbidden - insufficient permissions',
    '404': 'Not Found',
    '500': 'Internal Server Error',
    '503': 'Service Unavailable',
  };

  return descriptions[code] || `Unknown error: ${code}`;
}

/**
 * Parse Prometheus error metrics
 */
function parseErrorMetrics(text: string, totalRequests: number): ErrorSummary {
  const lines = text.split('\n');
  const errors: ParsedErrors = {};
  let totalErrors = 0;

  for (const line of lines) {
    if (line.startsWith('#') || !line.trim()) continue;

    // Parse: apollo_router_graphql_error_total{code="FERP-9001",otel_scope_name="apollo/router"} 43
    const match = line.match(/apollo_router_graphql_error_total\{code="([^"]+)"[^}]*\}\s+([0-9]+)/);
    if (match) {
      const [, code, count] = match;
      // @ts-expect-error Argument type
      const numCount = parseInt(count, 10);
      // @ts-expect-error TS2538
      errors[code] = (errors[code] || 0) + numCount;
      totalErrors += numCount;
    }

    // Also capture generic errors without code
    if (line.match(/apollo_router_graphql_error_total\{otel_scope_name/) && !line.includes('code=')) {
      const match2 = line.match(/\}\s+([0-9]+)/);
      if (match2) {
        // @ts-expect-error Argument type
        const count = parseInt(match2[1], 10);
        errors['UNKNOWN'] = (errors['UNKNOWN'] || 0) + count;
        totalErrors += count;
      }
    }

    // Capture HTTP errors
    const httpMatch = line.match(/apollo_router_http_requests_total\{.*status="([45]\d{2})".*\}\s+([0-9]+)/);
    if (httpMatch) {
      const [, status, count] = httpMatch;
      // @ts-expect-error Argument type
      const numCount = parseInt(count, 10);
      errors[`HTTP_${status}`] = (errors[`HTTP_${status}`] || 0) + numCount;
      totalErrors += numCount;
    }
  }

  // Build error traces
  const traces: ErrorTrace[] = Object.entries(errors).map(([code, count]) => ({
    code,
    count,
    percentage: totalRequests > 0 ? (count / totalRequests) * 100 : 0,
    category: categorizeError(code),
    severity: getErrorSeverity(code, count),
    description: getErrorDescription(code),
    timestamp: new Date(),
  }));

  // Sort by count descending
  traces.sort((a, b) => b.count - a.count);

  // Aggregate by category
  const byCategory = traces.reduce((acc, trace) => {
    acc[trace.category] = (acc[trace.category] || 0) + trace.count;
    return acc;
  }, {} as Record<string, number>);

  // Aggregate by severity
  const bySeverity = traces.reduce((acc, trace) => {
    acc[trace.severity] = (acc[trace.severity] || 0) + trace.count;
    return acc;
  }, {} as Record<string, number>);

  const errorRate = totalRequests > 0 ? (totalErrors / totalRequests) * 100 : 0;

  return {
    totalErrors,
    errorRate,
    byCategory,
    bySeverity,
    recentErrors: traces.slice(0, 10), // Most recent 10
    topErrors: traces.slice(0, 5), // Top 5 by count
    allErrors: traces, // All errors for full table display
  };
}

/**
 * Hook to fetch and track errors
 */
export function useErrorTrace(refreshIntervalMs = 10000) {
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchErrors = useCallback(async () => {
    try {
      const response = await fetch('/router-metrics');
      if (!response.ok) {
        throw new Error(`Router metrics unavailable: ${response.status}`);
      }

      const text = await response.text();

      // Also get total requests for error rate calculation
      let totalRequests = 0;
      const requestMatches = text.matchAll(/apollo_router_http_requests_total\{status="200"[^}]*\}\s+([0-9]+)/g);
      for (const match of requestMatches) {
        // @ts-expect-error Argument type
        totalRequests += parseInt(match[1], 10);
      }

      const parsed = parseErrorMetrics(text, totalRequests);
      setSummary(parsed);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch error trace:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  // Refresh interval
  useEffect(() => {
    if (refreshIntervalMs <= 0) return;

    const interval = setInterval(fetchErrors, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [fetchErrors, refreshIntervalMs]);

  return {
    summary,
    loading,
    error,
    refetch: fetchErrors,
  };
}

export default useErrorTrace;
